import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { DesktopModeProvider } from "@/contexts/DesktopModeContext";
import { EasyModeProvider } from "@/contexts/EasyModeContext";
import { AccountingProvider } from "@/contexts/AccountingContext";
import { CompanyProvider } from "@/contexts/CompanyContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { DocumentProvider } from "@/contexts/DocumentContext";
import { ReconciliationProvider } from "@/contexts/ReconciliationContext";
import { InventoryProvider } from "@/contexts/InventoryContext";
import { ExpenseProvider } from "@/contexts/ExpenseContext";
import { PayrollProvider } from "@/contexts/PayrollContext";
import { AutomationProvider } from "@/contexts/AutomationContext";
import { ClientProvider } from "@/contexts/ClientContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ReferralProvider } from "@/contexts/ReferralContext";
import Index from "./pages/Index";
import LoginPage from "./pages/LoginPage";
import GetStartedPage from "./pages/GetStartedPage";
import NotFound from "./pages/NotFound";
import DashboardLayout from "./components/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import LedgersPage from "./pages/dashboard/LedgersPage";
import JournalsPage from "./pages/dashboard/JournalsPage";
import SalesPage from "./pages/dashboard/SalesPage";
import PurchasePage from "./pages/dashboard/PurchasePage";
import InvoicesPage from "./pages/dashboard/InvoicesPage";
import InventoryPage from "./pages/dashboard/InventoryPage";
import GSTPage from "./pages/dashboard/GSTPage";
import EWayBillsPage from "./pages/dashboard/EWayBillsPage";
import ReportsPage from "./pages/dashboard/ReportsPage";
import AuditLogsPage from "./pages/dashboard/AuditLogsPage";
import CompaniesPage from "./pages/dashboard/CompaniesPage";
import UsersPage from "./pages/dashboard/UsersPage";
import SubscriptionPage from "./pages/dashboard/SubscriptionPage";
import SettingsPage from "./pages/dashboard/SettingsPage";
import GSTReportsPage from "./pages/dashboard/GSTReportsPage";
import DocumentsPage from "./pages/dashboard/DocumentsPage";
import ReconciliationPage from "./pages/dashboard/ReconciliationPage";
import ExpensesPage from "./pages/dashboard/ExpensesPage";
import PayrollPage from "./pages/dashboard/PayrollPage";
import AutomationPage from "./pages/dashboard/AutomationPage";
import ClientsPage from "./pages/dashboard/ClientsPage";
import PartiesPage from "./pages/dashboard/PartiesPage";
import ImportPage from "./pages/dashboard/ImportPage";
import ExportPage from "./pages/dashboard/ExportPage";
import NotificationsPage from "./pages/dashboard/NotificationsPage";
import InsightsPage from "./pages/dashboard/InsightsPage";
import SecurityPage from "./pages/dashboard/SecurityPage";
import BackupsPage from "./pages/dashboard/BackupsPage";
import IntegrationsPage from "./pages/dashboard/IntegrationsPage";
import WhiteLabelPage from "./pages/dashboard/WhiteLabelPage";
import OnboardingPage from "./pages/dashboard/OnboardingPage";
import GSTConnectPage from "./pages/dashboard/GSTConnectPage";
import EInvoicePage from "./pages/dashboard/EInvoicePage";
import GSTReconciliationPage from "./pages/dashboard/GSTReconciliationPage";
import AIAssistantPage from "./pages/dashboard/AIAssistantPage";
import AIAutoEntryPage from "./pages/dashboard/AIAutoEntryPage";
import AITaxAdvisorPage from "./pages/dashboard/AITaxAdvisorPage";
import AIFraudDetectionPage from "./pages/dashboard/AIFraudDetectionPage";
import AIReportGeneratorPage from "./pages/dashboard/AIReportGeneratorPage";
import AISearchPage from "./pages/dashboard/AISearchPage";
import VoiceAccountingPage from "./pages/dashboard/VoiceAccountingPage";
import ReferralDashboardPage from "./pages/dashboard/ReferralDashboardPage";
import CAMarketplacePage from "./pages/dashboard/CAMarketplacePage";
import CADiscoveryPage from "./pages/dashboard/CADiscoveryPage";
import CAProfileEditPage from "./pages/dashboard/CAProfileEditPage";
import CAClientManagementPage from "./pages/dashboard/CAClientManagementPage";
import CAAccessApprovalsPage from "./pages/dashboard/CAAccessApprovalsPage";
import ServiceRequestsPage from "./pages/dashboard/ServiceRequestsPage";
import MarketplaceTransactionsPage from "./pages/dashboard/MarketplaceTransactionsPage";
import CAVerificationPage from "./pages/dashboard/CAVerificationPage";
import CAServicePackagesPage from "./pages/dashboard/CAServicePackagesPage";
import UPIReconciliationPage from "./pages/dashboard/UPIReconciliationPage";
import BankSyncPage from "./pages/dashboard/BankSyncPage";
import PaymentGatewaysPage from "./pages/dashboard/PaymentGatewaysPage";
import PaymentTrackingPage from "./pages/dashboard/PaymentTrackingPage";
import CashFlowPage from "./pages/dashboard/CashFlowPage";
import APIDeveloperPage from "./pages/dashboard/APIDeveloperPage";
import SyncDashboardPage from "./pages/dashboard/SyncDashboardPage";
import DesktopAppPage from "./pages/dashboard/DesktopAppPage";
import BranchesPage from "./pages/dashboard/BranchesPage";
import BranchPermissionsPage from "./pages/dashboard/BranchPermissionsPage";
import InterBranchAccountingPage from "./pages/dashboard/InterBranchAccountingPage";
import BranchInventoryPage from "./pages/dashboard/BranchInventoryPage";
import { OfflineProvider } from "@/contexts/OfflineContext";
import { BranchProvider } from "@/contexts/BranchContext";
import { PartyProvider } from "@/contexts/PartyContext";
import { CAPartnerProvider } from "@/contexts/CAPartnerContext";
import { AffiliateProvider } from "@/contexts/AffiliateContext";
import { CommissionMgmtProvider } from "@/contexts/CommissionMgmtContext";
import CAPartnerDashboardPage from "./pages/dashboard/CAPartnerDashboardPage";
import AffiliateDashboardPage from "./pages/dashboard/AffiliateDashboardPage";
import CommissionManagementPage from "./pages/dashboard/CommissionManagementPage";
import LeadGenerationPage from "./pages/dashboard/LeadGenerationPage";
import AccouProMarketplacePage from "./pages/dashboard/AccouProMarketplacePage";
import EducationHubPage from "./pages/dashboard/EducationHubPage";
import CommunityForumPage from "./pages/dashboard/CommunityForumPage";
import EmailNotificationsPage from "./pages/dashboard/EmailNotificationsPage";
import AnnouncementsPage from "./pages/dashboard/AnnouncementsPage";
import FeatureRequestsPage from "./pages/dashboard/FeatureRequestsPage";
import AnalyticsDashboardPage from "./pages/dashboard/AnalyticsDashboardPage";
import FeedbackPage from "./pages/dashboard/FeedbackPage";
import ContactPage from "./pages/ContactPage";
import ProfilePage from "./pages/dashboard/ProfilePage";
import SystemTestPage from "./pages/dashboard/SystemTestPage";

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary fallbackTitle="AccouPro System Notice">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
      <AuthProvider>
        <DesktopModeProvider>
        <EasyModeProvider>
        <CompanyProvider>
        <AccountingProvider>
        <PartyProvider>
        <DocumentProvider>
        <InventoryProvider>
        <ReconciliationProvider>
        <ExpenseProvider>
        <PayrollProvider>
        <AutomationProvider>
        <ClientProvider>
        <NotificationProvider>
        <SubscriptionProvider>
        <OfflineProvider>
        <BranchProvider>
<ReferralProvider>
        <CAPartnerProvider>
        <AffiliateProvider>
        <CommissionMgmtProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/get-started" element={<GetStartedPage />} />
              <Route path="/contact" element={<ContactPage />} />
              
              {/* Protected routes */}
              <Route path="/dashboard" element={<ProtectedRoute><ErrorBoundary><DashboardLayout /></ErrorBoundary></ProtectedRoute>}>
                <Route index element={<DashboardHome />} />
                <Route path="companies" element={<CompaniesPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="ledgers" element={<LedgersPage />} />
                <Route path="journals" element={<JournalsPage />} />
                <Route path="vouchers" element={<JournalsPage />} />
                <Route path="contra" element={<JournalsPage />} />
                <Route path="payment" element={<JournalsPage />} />
                <Route path="payments" element={<JournalsPage />} />
                <Route path="receipt" element={<JournalsPage />} />
                <Route path="receipts" element={<JournalsPage />} />
                <Route path="stock-journals" element={<JournalsPage />} />
                <Route path="stock-adjustments" element={<JournalsPage />} />
                <Route path="credit-notes" element={<JournalsPage />} />
                <Route path="debit-notes" element={<JournalsPage />} />
                <Route path="delivery-notes" element={<JournalsPage />} />
                <Route path="receipt-notes" element={<JournalsPage />} />
                <Route path="physical-stock" element={<JournalsPage />} />
                <Route path="sales" element={<SalesPage />} />
                <Route path="purchase" element={<PurchasePage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="inventory" element={<InventoryPage />} />
                <Route path="gst" element={<GSTPage />} />
                <Route path="gst-reports" element={<GSTReportsPage />} />
                <Route path="eway-bills" element={<EWayBillsPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="audit-logs" element={<AuditLogsPage />} />
                <Route path="subscription" element={<SubscriptionPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="reconciliation" element={<ReconciliationPage />} />
                <Route path="expenses" element={<ExpensesPage />} />
                <Route path="payroll" element={<PayrollPage />} />
                <Route path="automation" element={<AutomationPage />} />
                <Route path="customers" element={<PartiesPage />} />
                <Route path="customer" element={<PartiesPage />} />
                <Route path="suppliers" element={<PartiesPage />} />
                <Route path="supplier" element={<PartiesPage />} />
                <Route path="clients" element={<PartiesPage />} />
                <Route path="client" element={<PartiesPage />} />
                <Route path="parties" element={<PartiesPage />} />
                <Route path="party" element={<PartiesPage />} />
                <Route path="import" element={<ImportPage />} />
                <Route path="export" element={<ExportPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="insights" element={<InsightsPage />} />
                <Route path="security" element={<SecurityPage />} />
                <Route path="backups" element={<BackupsPage />} />
                <Route path="integrations" element={<IntegrationsPage />} />
                <Route path="white-label" element={<WhiteLabelPage />} />
                <Route path="onboarding" element={<OnboardingPage />} />
                <Route path="gst-connect" element={<GSTConnectPage />} />
                <Route path="e-invoice" element={<EInvoicePage />} />
                <Route path="gst-reconciliation" element={<GSTReconciliationPage />} />
                <Route path="ai-assistant" element={<AIAssistantPage />} />
                <Route path="ai-auto-entry" element={<AIAutoEntryPage />} />
                <Route path="ai-tax-advisor" element={<AITaxAdvisorPage />} />
                <Route path="ai-fraud-detection" element={<AIFraudDetectionPage />} />
                <Route path="ai-reports" element={<AIReportGeneratorPage />} />
                <Route path="ai-search" element={<AISearchPage />} />
                <Route path="voice-accounting" element={<VoiceAccountingPage />} />
                <Route path="ca-marketplace" element={<CAMarketplacePage />} />
                <Route path="ca-discovery" element={<CADiscoveryPage />} />
                <Route path="ca-profile" element={<CAProfileEditPage />} />
                <Route path="ca-clients" element={<CAClientManagementPage />} />
                <Route path="ca-access-approvals" element={<CAAccessApprovalsPage />} />
                <Route path="service-requests" element={<ServiceRequestsPage />} />
                <Route path="marketplace-transactions" element={<MarketplaceTransactionsPage />} />
                <Route path="ca-verification" element={<CAVerificationPage />} />
                <Route path="ca-packages" element={<CAServicePackagesPage />} />
                <Route path="upi-reconciliation" element={<UPIReconciliationPage />} />
                <Route path="bank-sync" element={<BankSyncPage />} />
                <Route path="payment-gateways" element={<PaymentGatewaysPage />} />
                <Route path="payment-tracking" element={<PaymentTrackingPage />} />
                <Route path="cash-flow" element={<CashFlowPage />} />
                <Route path="api-developer" element={<APIDeveloperPage />} />
                <Route path="sync" element={<SyncDashboardPage />} />
                <Route path="desktop-app" element={<DesktopAppPage />} />
                <Route path="branches" element={<BranchesPage />} />
                <Route path="branch-permissions" element={<BranchPermissionsPage />} />
                <Route path="inter-branch" element={<InterBranchAccountingPage />} />
                <Route path="branch-inventory" element={<BranchInventoryPage />} />
                <Route path="referrals" element={<ReferralDashboardPage />} />
                <Route path="ca-partner" element={<CAPartnerDashboardPage />} />
                <Route path="affiliate" element={<AffiliateDashboardPage />} />
                <Route path="commissions" element={<CommissionManagementPage />} />
                <Route path="leads" element={<LeadGenerationPage />} />
                <Route path="marketplace-services" element={<AccouProMarketplacePage />} />
                <Route path="education" element={<EducationHubPage />} />
                <Route path="community" element={<CommunityForumPage />} />
                <Route path="email-notifications" element={<EmailNotificationsPage />} />
                <Route path="announcements" element={<AnnouncementsPage />} />
                <Route path="feature-requests" element={<FeatureRequestsPage />} />
                <Route path="analytics" element={<AnalyticsDashboardPage />} />
                <Route path="feedback" element={<FeedbackPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="system-test" element={<SystemTestPage />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </CommissionMgmtProvider>
        </AffiliateProvider>
        </CAPartnerProvider>
        </ReferralProvider>
        </BranchProvider>
        </OfflineProvider>
        </SubscriptionProvider>
        </NotificationProvider>
        </ClientProvider>
        </AutomationProvider>
        </PayrollProvider>
        </ExpenseProvider>
        </ReconciliationProvider>
        </InventoryProvider>
        </DocumentProvider>
        </PartyProvider>
        </AccountingProvider>
        </CompanyProvider>
        </EasyModeProvider>
        </DesktopModeProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
